import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { AuthContext } from './auth-context';
import useApi from '../services/api';

const OnboardingContext = createContext({});

function OnboardingProvider({ children }) {
    const { user, userDetails, reloadUserDetails } = useContext(AuthContext);
    const { updateTutorialFlags } = useApi();
    
    // Tutorial flags from server or localStorage fallback
    const [tutorialFlags, setTutorialFlags] = useState(() => {
        // Always check localStorage first for existing flags
        const stored = localStorage.getItem('plancrafted_hints_viewed');
        const localFlags = stored ? JSON.parse(stored) : {};
        
        // If user is logged in and has server flags, merge them
        if (user && userDetails?.tutorial_flags) {
            return { ...localFlags, ...userDetails.tutorial_flags };
        }
        
        // Use local storage flags
        return localFlags;
    });

    // Track if user should see the "My Models" hint after creating first model
    const [shouldShowMyModelsHint, setShouldShowMyModelsHint] = useState(false);
    
    // Track if user should see the "Upload Photos" hint on plans page
    const [shouldShowUploadPhotosHint, setShouldShowUploadPhotosHint] = useState(false);
    

    // Sync tutorial flags when user details change
    useEffect(() => {
        if (user && userDetails?.tutorial_flags) {
            setTutorialFlags(userDetails.tutorial_flags);
        }
    }, [user, userDetails?.tutorial_flags]);

    // Mark a hint as viewed
    const markHintAsViewed = useCallback(async (hintId) => {
        const newFlags = { ...tutorialFlags, [hintId]: true };
        setTutorialFlags(newFlags);

        if (user) {
            // Update server for logged-in users
            try {
                await updateTutorialFlags({ [hintId]: true });
                // Reload user details to get the updated tutorial flags
                await reloadUserDetails();
            } catch (error) {
                console.error('Failed to update tutorial flag on server:', error);
                // Revert local state on error
                setTutorialFlags(tutorialFlags);
            }
        } else {
            // Update localStorage for logged-out users
            localStorage.setItem('plancrafted_hints_viewed', JSON.stringify(newFlags));
        }
    }, [tutorialFlags, user, updateTutorialFlags, reloadUserDetails]);

    // Check if a hint has been viewed
    const hasViewedHint = useCallback((hintId) => {
        return !!tutorialFlags[hintId];
    }, [tutorialFlags]);

    // Reset all hints (useful for testing or user preference)
    const resetAllHints = useCallback(async () => {
        const resetFlags = {};
        setTutorialFlags(resetFlags);

        if (user) {
            // Reset on server for logged-in users
            try {
                await updateTutorialFlags({
                    firstModelCreated: false,
                    uploadPhotosIntro: false,
                    planGenerationIntro: false,
                    checkoutCompleted: false
                });
                // Reload user details to get the updated tutorial flags
                await reloadUserDetails();
            } catch (error) {
                console.error('Failed to reset tutorial flags on server:', error);
            }
        } else {
            // Reset localStorage for logged-out users
            localStorage.removeItem('plancrafted_hints_viewed');
        }
    }, [user, updateTutorialFlags, reloadUserDetails]);

    // Expose reset function globally for testing
    if (typeof window !== 'undefined') {
        window.resetTutorialFlags = resetAllHints;
    }

    // Trigger the My Models hint after first model creation
    const triggerMyModelsHint = useCallback(() => {
        if (!hasViewedHint('firstModelCreated')) {
            setShouldShowMyModelsHint(true);
        }
    }, [hasViewedHint]);

    // Clear the My Models hint trigger
    const clearMyModelsHint = useCallback(() => {
        setShouldShowMyModelsHint(false);
        markHintAsViewed('firstModelCreated');
    }, [markHintAsViewed]);

    // Trigger the Upload Photos hint when user visits plans page with at least one plan
    const triggerUploadPhotosHint = useCallback(() => {
        if (!hasViewedHint('uploadPhotosIntro')) {
            setShouldShowUploadPhotosHint(true);
        }
    }, [hasViewedHint]);

    // Clear the Upload Photos hint trigger
    const clearUploadPhotosHint = useCallback(() => {
        setShouldShowUploadPhotosHint(false);
        markHintAsViewed('uploadPhotosIntro');
    }, [markHintAsViewed]);


    const value = useMemo(() => ({
        tutorialFlags,
        hasViewedHint,
        markHintAsViewed,
        resetAllHints,
        shouldShowMyModelsHint,
        triggerMyModelsHint,
        clearMyModelsHint,
        shouldShowUploadPhotosHint,
        triggerUploadPhotosHint,
        clearUploadPhotosHint
    }), [tutorialFlags, hasViewedHint, markHintAsViewed, resetAllHints, shouldShowMyModelsHint, triggerMyModelsHint, clearMyModelsHint, shouldShowUploadPhotosHint, triggerUploadPhotosHint, clearUploadPhotosHint]);

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
}

// Custom hook for using onboarding context
const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within OnboardingProvider');
    }
    return context;
};

export { OnboardingProvider, OnboardingContext, useOnboarding };